/**
 * AI Review Service
 * Prepares trade data for AI review analysis and calls backend endpoint
 */

import { calculateProfitLoss } from './enrichmentService';
import { getETNow, getETDateString } from '../utils/marketUtils';
import { AI_PROVIDER, AI_SOURCE } from '../constants/ai';

/**
 * Get followed recommendation with backward compatibility
 * For legacy data without followedRecommendation field, infer from boolean flags
 * @param {Object} aiRec - AI recommendations object
 * @returns {string|null} - AI_SOURCE id ('claude' / 'gpt' / 'both' / 'manual') or null
 */
const getFollowedRecommendation = (aiRec) => {
  if (!aiRec) return AI_SOURCE.MANUAL;

  // If new field exists, use it
  if (aiRec.followedRecommendation) {
    return aiRec.followedRecommendation;
  }

  // Legacy fallback: infer from boolean flags
  if (aiRec.claudeRecommended && aiRec.gptRecommended) {
    return AI_SOURCE.BOTH; // Assume consensus when both recommended
  } else if (aiRec.claudeRecommended) {
    return AI_SOURCE.CLAUDE;
  } else if (aiRec.gptRecommended) {
    return AI_SOURCE.GPT;
  } else {
    return AI_SOURCE.MANUAL;
  }
};

/**
 * Filter records by time range
 * @param {Array} records - All trade records
 * @param {string} timeRange - 'day', 'week', 'month'
 * @returns {Array} - Filtered records
 */
const filterByTimeRange = (records, timeRange) => {
  if (!records || records.length === 0) return [];

  const now = getETNow();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  let startDate;
  switch (timeRange) {
    case 'day':
      startDate = today;
      break;
    case 'week':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);
      break;
    case 'month':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 30);
      break;
    default:
      return records;
  }

  return records.filter(record => {
    const recordDate = new Date(record.timestamp);
    return recordDate >= startDate;
  });
};

/**
 * Prepare trade data for AI review
 * @param {Array} records - All UI trade records
 * @param {string} timeRange - 'day', 'week', 'month'
 * @returns {Object} - Aggregated trade data with complete AI details
 */
export const prepareReviewData = (records, timeRange) => {
  // Filter records by time range
  const filteredRecords = filterByTimeRange(records, timeRange);

  if (filteredRecords.length === 0) {
    return {
      timeRange,
      dateRange: {
        start: getETDateString(),
        end: getETDateString()
      },
      stats: {
        totalTrades: 0,
        entries: 0,
        exits: 0,
        aiRecommended: 0
      },
      pairs: []
    };
  }

  // Calculate statistics
  const stats = {
    totalTrades: filteredRecords.length,
    entries: filteredRecords.filter(r => r.action === 'entry').length,
    exits: filteredRecords.filter(r => r.action === 'exit').length,
    aiRecommended: filteredRecords.filter(r =>
      r.aiRecommendations?.claudeRecommended || r.aiRecommendations?.gptRecommended
    ).length
  };

  // Transform UI records to match format expected by calculateProfitLoss
  // Since we don't have broker data in AI Review, we create mock matches from UI records
  const mockMatches = filteredRecords.map(record => ({
    uiRecord: record,
    brokerTrade: null,
    confidence: 'high',
    brokerPrice: record.actualPrice,
    slippage: 0,
    fees: 0,
    matched: true
  }));

  // Extract paired trades (from enrichment service)
  const pairsResult = calculateProfitLoss(mockMatches);

  // Map pairs to include complete AI recommendation details
  const enrichedPairs = pairsResult.pairs.map(pair => ({
    symbol: pair.symbol,
    quantity: pair.quantity,
    entryPrice: pair.entryPrice,
    exitPrice: pair.exitPrice,
    profit: pair.profit,
    profitPercent: pair.profitPercent,
    fees: pair.fees || 0,
    duration: pair.duration,
    entryTimestamp: pair.entry.uiRecord.timestamp,
    exitTimestamp: pair.exit.uiRecord.timestamp,

    // Complete AI recommendation details (not just boolean flags)
    aiRecommendations: {
      claudeRecommended: pair.entry.uiRecord.aiRecommendations?.claudeRecommended || false,
      claudeRating: pair.entry.uiRecord.aiRecommendations?.claudeRating || null,
      claudeRank: pair.entry.uiRecord.aiRecommendations?.claudeRank || null,
      claudeTrend: pair.entry.uiRecord.aiRecommendations?.claudeTrend || null,
      claudePattern: pair.entry.uiRecord.aiRecommendations?.claudePattern || null,
      claudeAction: pair.entry.uiRecord.aiRecommendations?.claudeAction || null,
      claudeMarketSummary: pair.entry.uiRecord.aiRecommendations?.claudeMarketSummary || null,
      claudeMarketAnalysis: pair.entry.uiRecord.aiRecommendations?.claudeMarketAnalysis || null,

      gptRecommended: pair.entry.uiRecord.aiRecommendations?.gptRecommended || false,
      gptRating: pair.entry.uiRecord.aiRecommendations?.gptRating || null,
      gptRank: pair.entry.uiRecord.aiRecommendations?.gptRank || null,
      gptTrend: pair.entry.uiRecord.aiRecommendations?.gptTrend || null,
      gptPattern: pair.entry.uiRecord.aiRecommendations?.gptPattern || null,
      gptAction: pair.entry.uiRecord.aiRecommendations?.gptAction || null,
      gptMarketSummary: pair.entry.uiRecord.aiRecommendations?.gptMarketSummary || null,
      gptMarketAnalysis: pair.entry.uiRecord.aiRecommendations?.gptMarketAnalysis || null,

      // Which recommendation was followed (for accurate pattern accuracy calculation)
      followedRecommendation: getFollowedRecommendation(pair.entry.uiRecord.aiRecommendations),
      followedPattern: pair.entry.uiRecord.aiRecommendations?.followedPattern || null,
      followedSource: pair.entry.uiRecord.aiRecommendations?.followedSource || null
    },

    // Market context at entry
    entryContext: {
      marketSentiment: pair.entry.uiRecord.snapshot?.marketSentiment || null,
      indices: pair.entry.uiRecord.snapshot?.indices || {},
      score: pair.entry.uiRecord.snapshot?.score || null,
      dailyGain: pair.entry.uiRecord.snapshot?.dailyGain || null,
      volume: pair.entry.uiRecord.snapshot?.volume || null,
      accelerationArrow: pair.entry.uiRecord.snapshot?.accelerationArrow || null
    },

    // Market context at exit
    exitContext: {
      marketSentiment: pair.exit.uiRecord.snapshot?.marketSentiment || null,
      indices: pair.exit.uiRecord.snapshot?.indices || {},
      score: pair.exit.uiRecord.snapshot?.score || null,
      dailyGain: pair.exit.uiRecord.snapshot?.dailyGain || null,
      volume: pair.exit.uiRecord.snapshot?.volume || null,
      accelerationArrow: pair.exit.uiRecord.snapshot?.accelerationArrow || null
    }
  }));

  // Calculate date range
  const timestamps = filteredRecords.map(r => new Date(r.timestamp));
  const minDate = new Date(Math.min(...timestamps));
  const maxDate = new Date(Math.max(...timestamps));

  return {
    timeRange,
    dateRange: {
      start: minDate.toISOString().split('T')[0],
      end: maxDate.toISOString().split('T')[0]
    },
    stats,
    pairs: enrichedPairs
  };
};

/**
 * Prepare review data from simulated backtest trades
 * Maps simulated trade fields to the same review pairs format as prepareReviewDataFromEnriched
 * @param {Array} simulatedTrades - Individual simulated trades (enteredTrades from loadSimulatedSummary)
 * @param {string} timeRange - Time range label
 * @returns {Object} - Review data in standard format with dataSource='simulated'
 */
export const prepareReviewDataFromSimulated = (simulatedTrades, timeRange, totalTradeCount) => {
  if (!simulatedTrades || simulatedTrades.length === 0) {
    return {
      timeRange,
      dataSource: 'simulated',
      dateRange: { start: getETDateString(), end: getETDateString() },
      stats: { totalTrades: 0, entries: 0, exits: 0, aiRecommended: 0 },
      pairs: []
    };
  }

  const aiRecommended = simulatedTrades.filter(t =>
    t.claudeRecommended || t.gptRecommended
  ).length;

  const reviewPairs = simulatedTrades.map(trade => ({
    symbol: trade.symbol,
    quantity: trade.quantity || 100,
    entryPrice: trade.entryPrice,
    exitPrice: trade.exitPrice,
    profit: trade.profit,
    profitPercent: trade.profitPercent,
    fees: 0,
    duration: trade.duration ? `${trade.duration}m` : null,
    entryTimestamp: trade.entryTime,
    exitTimestamp: trade.exitTime,
    exitReason: trade.exitReason,

    aiRecommendations: {
      claudeRecommended: trade.claudeRecommended || false,
      claudeRating: trade.claudeRating || null,
      claudeRank: null,
      claudeTrend: null,
      claudePattern: trade.claudePattern || null,
      claudeAction: null,
      claudeMarketSummary: null,
      claudeMarketAnalysis: null,

      gptRecommended: trade.gptRecommended || false,
      gptRating: trade.gptRating || null,
      gptRank: null,
      gptTrend: null,
      gptPattern: trade.gptPattern || null,
      gptAction: null,
      gptMarketSummary: null,
      gptMarketAnalysis: null,

      followedRecommendation: trade.source || null,
      followedPattern: null,
      followedSource: null
    },

    // Simulated trades don't have market snapshots
    entryContext: null,
    exitContext: null
  }));

  // Calculate date range from trade dates
  const dates = simulatedTrades.map(t => t.date).filter(Boolean).sort();

  return {
    timeRange,
    dataSource: 'simulated',
    dateRange: {
      start: dates[0] || getETDateString(),
      end: dates[dates.length - 1] || getETDateString()
    },
    stats: {
      totalTrades: totalTradeCount || simulatedTrades.length,
      entered: simulatedTrades.length,
      aiRecommended
    },
    pairs: reviewPairs
  };
};

/**
 * Fetch AI review analysis from backend
 * @param {Object} reviewData - Prepared trade data
 * @returns {Promise<Object>} - Dual AI analysis results { claude: {...}, openai: {...} }
 */
export const fetchAiReview = async (reviewData) => {
  const AI_SERVICE_URL = process.env.REACT_APP_AI_SERVICE_URL || 'http://localhost:8080';

  try {
    // Request structured output format for comprehensive analysis
    const requestData = {
      ...reviewData,
      outputFormat: 'structured',  // Request structured sections
      sections: ['overallSummary', 'winAnalysis', 'lossAnalysis', 'actionableRecommendations']
    };

    const token = localStorage.getItem('stock_ai_jwt_token');
    const response = await fetch(`${AI_SERVICE_URL}/api/review/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json(); // { claude: {...}, openai: {...} }
  } catch (error) {
    console.error('AI Review Error:', error.message);
    return {
      anthropic: { provider: AI_PROVIDER.ANTHROPIC, error: error.message },
      openai: { provider: AI_PROVIDER.OPENAI, error: error.message }
    };
  }
};
